import React, { useState } from "react";
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  LinkIcon,
  EllipsisVerticalIcon,
  TrashIcon,
  ClockIcon,
  SignalIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "./LoadingSpinner";

const SocialAccountCard = ({
  account,
  platform,
  onDisconnect,
  onRefresh,
  isRefreshing,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMetrics, setShowMetrics] = useState(false);

  const getStatusConfig = (status, tokenStatus) => {
    if (status === "error" || tokenStatus === "expired") {
      return {
        icon: XCircleIcon,
        text: tokenStatus === "expired" ? "Token Expired" : "Connection Error",
        color: "text-red-600",
        bg: "bg-red-50",
        border: "border-red-200",
        action: "refresh",
      };
    }

    if (status === "warning" || tokenStatus === "expiring_soon") {
      return {
        icon: ExclamationTriangleIcon,
        text:
          tokenStatus === "expiring_soon"
            ? "Token Expiring Soon"
            : "Needs Attention",
        color: "text-amber-600",
        bg: "bg-amber-50",
        border: "border-amber-200",
        action: "refresh",
      };
    }

    return {
      icon: CheckCircleIcon,
      text: "Connected",
      color: "text-green-600",
      bg: "bg-green-50",
      border: "border-green-200",
      action: null,
    };
  };

  const formatLastSync = (timestamp) => {
    if (!timestamp) return "Never";

    const date = new Date(timestamp);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  const getConnectionQuality = (account) => {
    const { last_sync, error_count, token_expires_at } = account;

    if (!last_sync)
      return { level: "poor", color: "text-red-500", text: "No data" };

    const lastSyncDate = new Date(last_sync);
    const hoursSinceSync = (new Date() - lastSyncDate) / (1000 * 60 * 60);

    if (error_count > 5 || hoursSinceSync > 48) {
      return { level: "poor", color: "text-red-500", text: "Poor" };
    }

    if (error_count > 2 || hoursSinceSync > 12) {
      return { level: "fair", color: "text-amber-500", text: "Fair" };
    }

    return { level: "good", color: "text-green-500", text: "Excellent" };
  };

  const statusConfig = getStatusConfig(account.status, account.token_status);
  const connectionQuality = getConnectionQuality(account);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center ${platform.color}`}
            >
              <span className="text-xl">{platform.icon}</span>
            </div>
            <div>
              <h3 className="font-medium text-gray-900">{platform.name}</h3>
              <p className="text-sm text-gray-500">
                @{account.username || account.display_name}
              </p>
            </div>
          </div>

          {/* Dropdown Menu */}
          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <EllipsisVerticalIcon className="w-5 h-5" />
            </button>

            {showDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                <button
                  onClick={() => {
                    onRefresh();
                    setShowDropdown(false);
                  }}
                  disabled={isRefreshing}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <ArrowPathIcon
                    className={`w-4 h-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`}
                  />
                  Refresh Connection
                </button>
                <button
                  onClick={() => {
                    setShowMetrics(!showMetrics);
                    setShowDropdown(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <ChartBarIcon className="w-4 h-4 mr-2" />
                  View Metrics
                </button>
                <button
                  onClick={() => {
                    onDisconnect();
                    setShowDropdown(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <TrashIcon className="w-4 h-4 mr-2" />
                  Disconnect
                </button>
              </div>
            )}

            {/* Click outside to close dropdown */}
            {showDropdown && (
              <div
                className="fixed inset-0 z-5"
                onClick={() => setShowDropdown(false)}
              />
            )}
          </div>
        </div>

        {/* Status Badge */}
        <div
          className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium mt-3 ${statusConfig.bg} ${statusConfig.border} ${statusConfig.color} border`}
        >
          <StatusIcon className="w-3 h-3 mr-1" />
          {statusConfig.text}
        </div>
      </div>

      {/* Connection Stats */}
      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex items-center space-x-1">
              <ClockIcon className="w-3 h-3 text-gray-400" />
              <span className="text-xs text-gray-500">Last Sync</span>
            </div>
            <p className="text-sm font-medium text-gray-900 mt-1">
              {formatLastSync(account.last_sync)}
            </p>
          </div>

          <div>
            <div className="flex items-center space-x-1">
              <SignalIcon className="w-3 h-3 text-gray-400" />
              <span className="text-xs text-gray-500">Quality</span>
            </div>
            <p
              className={`text-sm font-medium mt-1 ${connectionQuality.color}`}
            >
              {connectionQuality.text}
            </p>
          </div>
        </div>

        {/* Permissions Preview */}
        <div>
          <p className="text-xs text-gray-500 mb-2">Access Permissions</p>
          <div className="flex flex-wrap gap-1">
            {platform.metrics.slice(0, 4).map((metric) => (
              <span
                key={metric}
                className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs"
              >
                {metric}
              </span>
            ))}
            {platform.metrics.length > 4 && (
              <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                +{platform.metrics.length - 4}
              </span>
            )}
          </div>
        </div>

        {/* Action Button */}
        {statusConfig.action === "refresh" && (
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="w-full mt-3 bg-primary-600 text-white px-3 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm font-medium transition-colors"
          >
            {isRefreshing ? <LoadingSpinner size="sm" /> : "Refresh Connection"}
          </button>
        )}

        {/* Token Expiry Warning */}
        {account.token_expires_at && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-3">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">Token expires</span>
              <span className="font-medium text-gray-700">
                {new Date(account.token_expires_at).toLocaleDateString()}
              </span>
            </div>
            <div className="mt-2 w-full bg-gray-200 rounded-full h-1">
              <div
                className="bg-primary-600 h-1 rounded-full transition-all duration-300"
                style={{
                  width: `${Math.max(
                    0,
                    Math.min(
                      100,
                      ((new Date(account.token_expires_at) - new Date()) /
                        (30 * 24 * 60 * 60 * 1000)) *
                        100,
                    ),
                  )}%`,
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Expanded Metrics Section */}
      {showMetrics && (
        <div className="border-t border-gray-100 p-4 bg-gray-50">
          <h4 className="font-medium text-gray-900 mb-3">Available Metrics</h4>
          <div className="grid grid-cols-2 gap-2">
            {platform.metrics.map((metric) => (
              <div
                key={metric}
                className="bg-white border border-gray-200 rounded-lg p-2"
              >
                <p className="text-xs font-medium text-gray-700">{metric}</p>
                <p className="text-xs text-gray-500">Tracking enabled</p>
              </div>
            ))}
          </div>

          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              <LinkIcon className="w-3 h-3 inline mr-1" />
              OAuth Scopes: {platform.oauth_scope}
            </p>
          </div>

          <button
            onClick={() => setShowMetrics(false)}
            className="w-full mt-3 text-primary-600 hover:text-primary-700 text-sm font-medium"
          >
            Hide Details
          </button>
        </div>
      )}
    </div>
  );
};

export default SocialAccountCard;
